// WYSIWYG chat composer. Uses a contenteditable div instead of a textarea
// so formatting (bold, italic, code, links, lists) renders visually as
// the user types. On send, innerHTML is converted to markdown via
// htmlToMarkdown() so the message body stored in Supabase is clean
// markdown (rendered by symbolize-markdown in the message timeline).
import { detectReferenceTrigger } from '../../functions/chat/messageBody.js'
import { htmlToMarkdown } from '../../functions/chat/htmlToMarkdown.js'
import { getSDK } from '../../functions/sdk.js'

// Normalize SDK list responses — different routes wrap data under
// different keys (`data`, `rows`, `events`, `notes`, `articles`, `tickets`).
const _normalizeList = (res) => {
  if (Array.isArray(res)) return res
  if (!res || typeof res !== 'object') return []
  return res.data || res.rows || res.events || res.notes || res.articles || res.tickets || []
}

// On-demand reference data cache — avoids re-fetching on every keystroke.
const _refCache = { tickets: null, events: null, notes: null, kb: null }
const _fetchRef = async (type, query) => {
  if (type === 'ticket') {
    if (!_refCache.tickets) {
      // migrated 2026-04-27 to SDK route workspaceProject.tickets/list
      const res = await getSDK().execute('workspaceProject.tickets', 'list', {
        filter: { deleted: false },
        options: { columns: 'id, number, title, state, column_key', order: { updated_at: 'desc' }, limit: 50 }
      })
      _refCache.tickets = _normalizeList(res)
      setTimeout(() => { _refCache.tickets = null }, 60000)
    }
    const q = (query || '').toLowerCase()
    return _refCache.tickets.filter(t =>
      !q || (t.title && t.title.toLowerCase().includes(q)) || (t.number && String(t.number).includes(q))
    ).slice(0, 6)
  }
  if (type === 'event') {
    if (!_refCache.events) {
      // migrated 2026-04-27 to SDK route workspaceProject.calendar/list
      const res = await getSDK().execute('workspaceProject.calendar', 'list', {
        filter: {},
        options: { columns: 'id, title, date, start_time, end_time', order: { date: 'desc' }, limit: 50 }
      })
      _refCache.events = _normalizeList(res)
      setTimeout(() => { _refCache.events = null }, 60000)
    }
    const q = (query || '').toLowerCase()
    return _refCache.events.filter(ev =>
      !q || (ev.title && ev.title.toLowerCase().includes(q))
    ).slice(0, 6)
  }
  if (type === 'reference') {
    if (!_refCache.notes) {
      // migrated 2026-04-27 to SDK route workspaceProject.documents.notes/list
      const res = await getSDK().execute('workspaceProject.documents.notes', 'list', {
        options: { columns: 'id, title', order: { updated_at: 'desc' }, limit: 30 }
      })
      _refCache.notes = _normalizeList(res).map(n => ({ ...n, _refType: 'note' }))
      setTimeout(() => { _refCache.notes = null }, 60000)
    }
    if (!_refCache.kb) {
      // migrated 2026-04-27 to SDK route workspaceProject.documents.kb/list
      const res = await getSDK().execute('workspaceProject.documents.kb', 'list', {
        options: { columns: 'id, title', order: { updated_at: 'desc' }, limit: 30 }
      })
      _refCache.kb = _normalizeList(res).map(a => ({ ...a, _refType: 'knowledge base' }))
      setTimeout(() => { _refCache.kb = null }, 60000)
    }
    const q = (query || '').toLowerCase()
    return [..._refCache.notes, ..._refCache.kb].filter(d =>
      !q || (d.title && d.title.toLowerCase().includes(q))
    ).slice(0, 6)
  }
  return []
}

// ── execCommand helpers ─────────────────────────────────────────────
// execCommand is deprecated but still the only synchronous, cross-browser
// way to manipulate selection formatting inside a contenteditable. Every
// major browser still supports it for these basic operations.

const _exec = (cmd, value) => document.execCommand(cmd, false, value || null)

const _findEditor = (el) => {
  let cur = el
  for (let i = 0; i < 8 && cur; i++) {
    if (cur.Box && cur.Box.Editor && cur.Box.Editor.node) return cur.Box.Editor.node
    if (cur.Editor && cur.Editor.node) return cur.Editor.node
    cur = cur.parent
  }
  return null
}

// Toolbar button — uses onMouseDown+preventDefault so the editor never loses
// focus; this preserves the selection/caret so execCommand can actually apply
// formatting. `isActive` reads queryCommandState against the (reactive)
// composerSelVersion signal so buttons highlight when their format is active.
const _isActiveCmd = (cmd, value) => {
  if (!cmd) return false
  try {
    if (cmd === 'formatBlock') {
      const block = (document.queryCommandValue('formatBlock') || '').toLowerCase()
      return block === (value || '').toLowerCase()
    }
    return document.queryCommandState(cmd)
  } catch (_) { return false }
}

const _btn = (icon, title, cmd, value) => ({
  extends: 'NavbarButton',
  padding: 'Y2',
  title,
  aria: { label: title },
  // Read the reactive signal so DOMQL re-evaluates background when selection
  // moves. The actual lookup goes through document.queryCommandState.
  background: (el, s) => {
    void s.root?.composerSelVersion
    return _isActiveCmd(cmd, value) ? 'overlay' : 'transparent'
  },
  color: (el, s) => {
    void s.root?.composerSelVersion
    return _isActiveCmd(cmd, value) ? 'title' : 'caption'
  },
  Icon: { icon },
  onMouseDown: (e, el) => {
    // Prevent the editor from losing focus on button press. This is the
    // critical piece — without it, execCommand has no selection to act on.
    e.preventDefault()
    e.stopPropagation()
    const editor = _findEditor(el.parent && el.parent.parent)
    if (editor) editor.focus()
    _exec(cmd, value)
  },
  onClick: (e) => { e.preventDefault() }
})

export const ChatComposer = {
  flow: 'y',
  padding: '0 B A B',
  background: 'transparent',
  gap: 'V',

  onCreate: (el, s) => {
    const root = s && s.root
    if (!root) return
    // selectionchange fires globally (URL bar, other inputs, sidebar labels).
    // Gate on composer focus so the toolbar buttons don't re-render on
    // unrelated selections. Also diff the resolved active-command set so
    // we only mutate state when formatting actually changed — moving the
    // caret through plain text produces no state update.
    const CMDS = ['bold', 'italic', 'strikeThrough', 'underline', 'insertUnorderedList', 'insertOrderedList']
    el._selListener = () => {
      const editor = el.Box?.Editor?.node
      if (!editor) return
      const active = document.activeElement
      if (active !== editor && !editor.contains(active)) return
      if (el._selRaf) return
      el._selRaf = requestAnimationFrame(() => {
        el._selRaf = null
        const r = el.state?.root
        if (!r) return
        let sig = ''
        try {
          for (const c of CMDS) sig += document.queryCommandState(c) ? '1' : '0'
          sig += '|' + ((document.queryCommandValue('formatBlock') || '').toLowerCase())
        } catch (_) { sig = '_err' }
        if (sig === el._lastCmdSig) return
        el._lastCmdSig = sig
        r.update({ composerSelVersion: (r.composerSelVersion || 0) + 1 })
      })
    }
    document.addEventListener('selectionchange', el._selListener)

    // Clear editor on channel switch. v3.14 onUpdate doesn't fire on root state
    // changes, so we check inside the selection rAF loop isn't useful either.
    // The poll is cheap (~5 calls/s, each one map lookup + string compare).
    el._channelTimer = setInterval(() => {
      const r = el.state?.root
      if (!r) return
      const editor = el.Box?.Editor?.node
      if (editor && r.activeChannelId && el._lastChannel !== r.activeChannelId) {
        el._lastChannel = r.activeChannelId
        editor.innerHTML = ''
      }
    }, 200)
  },
  onRemove: (el) => {
    if (el._selListener) document.removeEventListener('selectionchange', el._selListener)
    if (el._selRaf) cancelAnimationFrame(el._selRaf)
    if (el._channelTimer) clearInterval(el._channelTimer)
  },

  // Toolbar at the bottom.
  Toolbar: {
    flow: 'x',
    gap: 'V',
    align: 'center left',
    order: '10',

    Bold:       _btn('bold',          'Bold',           'bold'),
    Italic:     _btn('italic',        'Italic',         'italic'),
    Strike:     _btn('strikethrough', 'Strikethrough',  'strikeThrough'),
    Underline:  _btn('underline',     'Underline',      'underline'),
    Code: {
      extends: 'NavbarButton',
      padding: 'Y2',
      title: 'Code',
      aria: { label: 'Code' },
      background: (el, s) => {
        void s.root?.composerSelVersion
        // Active when selection is inside a <code> element.
        try {
          const sel = window.getSelection()
          if (!sel || !sel.rangeCount) return 'transparent'
          let n = sel.getRangeAt(0).startContainer
          while (n) {
            if (n.nodeType === 1 && n.tagName === 'CODE') return 'overlay'
            n = n.parentNode
          }
        } catch (_) {}
        return 'transparent'
      },
      Icon: { icon: 'code' },
      onMouseDown: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        const editor = _findEditor(el.parent && el.parent.parent)
        if (!editor) return
        editor.focus()
        const sel = window.getSelection()
        if (!sel || !sel.rangeCount || sel.isCollapsed) return
        const range = sel.getRangeAt(0)
        // Bail if selection spans block elements — surroundContents would throw.
        try {
          const code = document.createElement('code')
          code.style.fontFamily = 'monospace'
          code.style.background = 'rgba(255,255,255,0.08)'
          code.style.padding = '1px 4px'
          code.style.borderRadius = '3px'
          range.surroundContents(code)
          sel.collapseToEnd()
        } catch (_) {
          // Fallback: wrap extracted fragment.
          const frag = range.extractContents()
          const code = document.createElement('code')
          code.style.fontFamily = 'monospace'
          code.style.background = 'rgba(255,255,255,0.08)'
          code.style.padding = '1px 4px'
          code.style.borderRadius = '3px'
          code.appendChild(frag)
          range.insertNode(code)
          sel.collapseToEnd()
        }
      },
      onClick: (e) => { e.preventDefault() }
    },
    Link: {
      extends: 'NavbarButton',
      padding: 'Y2',
      title: 'Link',
      aria: { label: 'Link' },
      Icon: { icon: 'link' },
      // Capture the selection before the prompt steals focus — prompt()
      // collapses the selection, so we need to snapshot it first.
      onMouseDown: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        const editor = _findEditor(el.parent && el.parent.parent)
        if (!editor) return
        editor.focus()
        const sel = window.getSelection()
        const savedRange = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null
        const url = prompt('URL:')
        if (!url) return
        if (savedRange) {
          sel.removeAllRanges()
          sel.addRange(savedRange)
        }
        _exec('createLink', url)
      },
      onClick: (e) => { e.preventDefault() }
    },
    Quote:      _btn('quote',         'Blockquote',     'formatBlock', 'blockquote'),
    UList:      _btn('listBulleted',  'Bulleted list',  'insertUnorderedList'),
    OList:      _btn('listNumbered',  'Numbered list',  'insertOrderedList'),
    Heading:    _btn('heading',       'Heading',        'formatBlock', 'h5'),
    Rule: {
      extends: 'NavbarButton',
      padding: 'Y2',
      title: 'Horizontal rule',
      aria: { label: 'Horizontal rule' },
      Icon: { icon: 'minus' },
      onMouseDown: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        const editor = _findEditor(el.parent && el.parent.parent)
        if (editor) editor.focus()
        _exec('insertHorizontalRule')
      },
      onClick: (e) => { e.preventDefault() }
    },

    Spacer: { flex: '1' },

    AttachBtn: {
      extends: 'NavbarButton',
      padding: 'Y2',
      title: 'Attach file',
      aria: { label: 'Attach file' },
      Icon: { icon: 'attachment' },
      onMouseDown: (e) => { e.preventDefault(); e.stopPropagation() },
      onClick: (e, el) => {
        e.preventDefault()
        e.stopPropagation()
        const inputNode = el.parent && el.parent.HiddenFile && el.parent.HiddenFile.node
        if (inputNode) inputNode.click()
      }
    },

    // Cross-app: launch a meet call linked to the active chat channel.
    // Reuses an existing meet_room if one already exists for this channel,
    // otherwise creates a new one. The room is linked via meet_rooms.chat_channel_id
    // so the channel can later show a "call in progress" banner (future).
    StartCallBtn: {
      extends: 'NavbarButton',
      padding: 'Y2',
      title: 'Start call',
      aria: { label: 'Start call' },
      Icon: { icon: 'video' },
      hide: (el, s) => !s.root.activeChannelId,
      onClick: async (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        const root = s.root
        const channelId = root.activeChannelId
        if (!channelId) return
        const channel = (root.channels || []).find(c => c.id === channelId)
        const isDm = channel?.kind === 'dm' || channel?.kind === 'group_dm'
        const existing = (root.rooms || []).find(r => r.chatChannelId === channelId && !r.endedAt)
        let id = existing?.id
        if (!id) {
          id = await el.call('createRoom', {
            name: (channel?.name || (isDm ? 'DM' : 'Channel')) + ' call',
            kind: isDm ? 'dm' : 'room',
            chatChannelId: channelId,
            // DMs default transcribe-off (privacy); channels default on.
            transcribeEnabled: !isDm
          })
        }
        if (!id) return
        root.update({
          activeRoomId: id,
          meetView: 'call'
        })
        if (typeof window !== 'undefined') {
          window.location.hash = `room=${id}&view=call`
        }
        let __root = el
        while (__root && !__root.routes) __root = __root.parent
        if (__root) el.router('/meet', __root)
        await el.call('connectCall', id)
      }
    },

    HiddenFile: {
      tag: 'input',
      type: 'file',
      multiple: true,
      accept: '*/*',
      display: 'none',
      onChange: (e, el, s) => {
        const root = s && s.root
        if (!root || !root.activeChannelId) return
        const files = [...(e.target.files || [])]
        for (const file of files) {
          el.call('uploadAttachment', { channelId: root.activeChannelId, file })
        }
        e.target.value = ''
      }
    }
  },

  Box: {
    flow: 'y',
    position: 'relative',
    padding: 'A A2 C1',
    round: 'A',
    overflow: 'hidden',
    theme: 'field',
    transition: 'B all defaultBezier',

    // Pending attachment chips — inside Box above the Editor,
    // matching PromptGallery's position inside PromptTextarea.
    AttachmentBar: {
      flow: 'x wrap',
      gap: 'Z',
      margin: '0',
      if: (el, s) => {
        const root = s && s.root
        if (!root || !root.activeChannelId) return false
        const list = (root.pendingAttachments || {})[root.activeChannelId] || []
        return list.length > 0
      },
      childExtends: 'ChatAttachmentChip',
      childrenAs: 'state',
      childProps: { boxSize: 'D' },
      children: (el, s) => (s.root.pendingAttachments[s.root.activeChannelId] || [])
    },

    // CircleButton send at bottom-right.
    SendBtn: {
      extends: 'CircleButton',
      position: 'absolute',
      bottom: 'Z',
      right: 'Z',
      zIndex: '1',
      padding: 'Z',
      icon: 'chevronUp',
      Icon: { transform: 'rotate(45deg)' },
      '@dark': {
        background: 'white',
        color: 'title',
        ':hover': { background: 'gray14', color: 'gray1' },
        ':active': { background: 'gray13', color: 'gray1' }
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        const root = s && s.root
        if (!root || !root.activeChannelId) return
        const editor = el.parent && el.parent.Editor && el.parent.Editor.node
        if (!editor) return
        const html = editor.innerHTML || ''
        const body = htmlToMarkdown(html)
        const pending = (root.pendingAttachments || {})[root.activeChannelId] || []
        if (!body && pending.length === 0) return
        el.call('sendMessage', { channelId: root.activeChannelId, body })
        editor.innerHTML = ''
      }
    },

    Flex: null,
    Button: null,

    // The WYSIWYG editor — contenteditable div replaces the old textarea.
    // Formatting is applied visually via execCommand from the toolbar.
    // On send, innerHTML is converted to markdown via htmlToMarkdown().
    Editor: {
      tag: 'div',
      minWidth: '100%',
      minHeight: 'C',
      maxHeight: 'F',
      padding: '0',
      outline: 'none',
      fontSize: 'Z2',
      lineHeight: '1.5',
      overflow: 'auto',
      wordBreak: 'break-word',
      attr: {
        contenteditable: 'true',
        'data-composer-input': 'true',
        'data-placeholder': (el, s) => {
          const root = s && s.root
          if (!root) return 'Message'
          const ch = (root.channels || []).find(c => c.id === root.activeChannelId)
          if (!ch) return 'Message'
          if (ch.kind === 'channel') return `Message #${ch.name || ''}`
          return 'Message'
        },
        spellcheck: 'true'
      },
      // Show placeholder when empty via CSS pseudo-element.
      ':empty::before': {
        content: 'attr(data-placeholder)',
        color: 'caption',
        pointerEvents: 'none'
      },
      onInput: (e, el, s) => {
        const root = s && s.root
        if (!root) return
        // Mention autocomplete.
        const sel = window.getSelection()
        if (!sel || !sel.rangeCount) return
        const range = sel.getRangeAt(0)
        const textNode = range.startContainer
        // Only detect mentions when the caret is inside THIS editor.
        // When the user clicks the mention dropdown, the selection moves
        // outside the editor and we must NOT null the picker — otherwise
        // the pick handler finds picker=null and can't do the replacement.
        if (!el.node.contains(textNode)) return
        if (textNode.nodeType !== 3) {
          if (root.mentionPicker) root.update({ mentionPicker: null })
          return
        }
        const text = textNode.textContent || ''
        const caret = range.startOffset
        const trig = detectReferenceTrigger(text, caret)
        if (!trig) {
          if (root.mentionPicker) root.update({ mentionPicker: null })
          return
        }
        const q = (trig.query || '').trim().toLowerCase()
        let options = []
        if (trig.type === 'person') {
          options = el.call('activeMembers').filter(p =>
            !q || (p.email && p.email.toLowerCase().split('@')[0].includes(q)) ||
            (p.name && p.name.toLowerCase().includes(q))
          ).slice(0, 6)
        } else if (trig.type === 'channel') {
          options = (root.channels || []).filter(c =>
            c.kind === 'channel' && (!q || (c.name && c.name.toLowerCase().includes(q)))
          ).slice(0, 6)
        } else if (trig.type === 'ticket' || trig.type === 'event' || trig.type === 'reference') {
          // Fetch on-demand — async, updates picker when ready.
          _fetchRef(trig.type, q).then(fetched => {
            if (!root.mentionPicker || root.mentionPicker.type !== trig.type) return
            root.update({
              mentionPicker: { ...root.mentionPicker, options: fetched }
            })
          })
          // Show empty picker immediately so position is set.
          options = []
        }
        const rect = el.node.getBoundingClientRect()
        root.update({
          mentionPicker: {
            type: trig.type,
            query: trig.query,
            start: trig.start,
            end: trig.end,
            top: rect.top - 240,
            left: rect.left,
            options,
            activeIndex: 0
          }
        })
      },
      onKeydown: (e, el, s) => {
        const root = s && s.root
        // ── Mention picker keyboard nav ──
        const picker = root && root.mentionPicker
        if (picker && picker.options && picker.options.length) {
          const opts = picker.options
          const idx = picker.activeIndex || 0
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            root.update({ mentionPicker: { ...picker, activeIndex: (idx + 1) % opts.length } })
            return
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault()
            root.update({ mentionPicker: { ...picker, activeIndex: (idx - 1 + opts.length) % opts.length } })
            return
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault()
            // Simulate pointerdown pick via the module-level handler.
            const rows = document.querySelectorAll('[data-mention-pick]')
            const row = rows[idx]
            if (row) row.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true, cancelable: true, view: window }))
            return
          }
          if (e.key === 'Escape') {
            root.update({ mentionPicker: null })
            return
          }
        }
        // Formatting shortcuts.
        if (e.metaKey || e.ctrlKey) {
          const k = e.key.toLowerCase()
          if (k === 'b') { e.preventDefault(); _exec('bold'); return }
          if (k === 'i') { e.preventDefault(); _exec('italic'); return }
          if (k === 'u') { e.preventDefault(); _exec('underline'); return }
        }
        if (e.key === 'Escape' && root && root.mentionPicker) {
          root.update({ mentionPicker: null })
          return
        }
        // Enter = send (Shift+Enter = newline).
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          if (!root || !root.activeChannelId) return
          const html = el.node.innerHTML || ''
          const body = htmlToMarkdown(html)
          const pending = (root.pendingAttachments || {})[root.activeChannelId] || []
          if (!body && pending.length === 0) return
          el.call('sendMessage', { channelId: root.activeChannelId, body })
          el.node.innerHTML = ''
          if (root.mentionPicker) root.update({ mentionPicker: null })
        }
      },
      onPaste: (e, el, s) => {
        const root = s && s.root
        if (!root || !root.activeChannelId) return
        // Handle pasted images.
        const items = (e.clipboardData && e.clipboardData.items) || []
        for (const it of items) {
          if (it.kind === 'file') {
            const file = it.getAsFile()
            if (file) {
              e.preventDefault()
              el.call('uploadAttachment', { channelId: root.activeChannelId, file })
              return
            }
          }
        }
        // For text paste, strip rich HTML and paste as plain text to keep
        // the editor clean (avoid pasting styled content from other apps).
        const text = e.clipboardData && e.clipboardData.getData('text/plain')
        if (text) {
          e.preventDefault()
          _exec('insertText', text)
        }
      }
    }
  }
}

export const ChatAttachmentChip = {
  flow: 'x',
  align: 'center left',
  gap: 'V',
  padding: 'V Z',
  round: 'Z',
  borderWidth: '1px',
  borderColor: 'line',
  borderStyle: 'solid',
  background: 'document',
  attr: { 'data-attachment-id': (el, s) => (s && s.id) || '' },

  Name: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'title',
    maxWidth: 'F',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s && s.name) || ''
  },
  Status: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => (s && s.uploading) ? '...' : ''
  },
  RemoveBtn: {
    tag: 'button',
    type: 'button',
    text: 'x',
    padding: '0 V',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'caption',
    fontSize: 'Z2',
    attr: { 'data-attachment-remove': (el, s) => (s && s.id) || '' },
    ':hover': { color: 'title' }
  }
}
